import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Mail, Phone, MapPin, MessageSquare, Send, Clock, Headphones } from 'lucide-react';
import { contactService } from '../../services/contactService';
import { siteSettingsService } from '../../services/siteSettingsService';
import { useToastContext } from '../../contexts/ToastContext';
import MarketingReveal from '../../components/home/MarketingReveal';
import {
  MarketingPage,
  MarketingMediaHero,
  MarketingSection,
  MarketingSplitIntro,
  MarketingFeatureGrid,
} from '../../components/home/MarketingPageChrome';

const CONTACT_HERO =
  'https://images.unsplash.com/photo-1521791136064-7986c2920216?auto=format&fit=crop&w=1800&q=80';

const ContactUs = () => {
  const toast = useToastContext();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    inquiryType: 'general',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const subject = searchParams.get('subject');
    if (subject) {
      setFormData((prev) => ({ ...prev, subject }));
      if (/vendor/i.test(subject)) setActiveTab('vendor');
    }
  }, [searchParams]);

  const { data: settingsResponse } = useQuery({
    queryKey: ['siteSettings'],
    queryFn: () => siteSettingsService.get(),
    staleTime: 5 * 60 * 1000,
  });
  const settings = settingsResponse?.data || {};
  const contactEmail = settings.site_email || 'hello@vendalist.com';
  const contactPhone = settings.site_phone || '1-800-VENDA-LIST';
  const contactAddress = settings.site_address || '123 Business Street';

  const tabs = [
    { id: 'general', label: 'General', icon: MessageSquare },
    { id: 'booking', label: 'Booking', icon: Mail },
    { id: 'technical', label: 'Technical', icon: Headphones },
    { id: 'vendor', label: 'Vendor', icon: MessageSquare },
  ];

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      description: `${contactEmail} — we respond within 24 hours.`,
      href: `mailto:${contactEmail}`,
    },
    {
      icon: Phone,
      title: 'Phone',
      description: `${contactPhone} · Mon–Fri, 9am–6pm EST`,
      href: `tel:${contactPhone.replace(/[^\d+]/g, '')}`,
    },
    {
      icon: MapPin,
      title: 'Address',
      description: `${contactAddress}, Accra, Ghana`,
    },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      await contactService.submit({
        ...formData,
        inquiryType: activeTab,
      });
      toast.success(null, "Thank you for your message! We'll get back to you soon.");
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
        inquiryType: activeTab,
      });
    } catch (err) {
      toast.error(
        null,
        err.response?.data?.message || 'Failed to send message. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <MarketingPage>
      <MarketingMediaHero
        title="Contact us"
        subtitle="Questions about bookings, vendors, or the product — send a note."
        image={CONTACT_HERO}
        height="sm"
      />

      <MarketingSection tone="white">
        <MarketingSplitIntro title="We’re easy to reach">
          <p>
            Pick a topic below so your message gets to the right team. Typical reply within one
            business day.
          </p>
        </MarketingSplitIntro>
      </MarketingSection>

      <MarketingSection tone="white" className="!pt-0 sm:!pt-0">
        <MarketingFeatureGrid items={contactInfo} />
      </MarketingSection>

      <MarketingSection tone="canvas">
        <MarketingReveal className="mx-auto max-w-3xl">
          <nav className="mb-6 flex gap-1 overflow-x-auto border-b border-m-border-light scrollbar-hide">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const active = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.id);
                    setFormData((prev) => ({ ...prev, inquiryType: tab.id }));
                  }}
                  className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-3 text-sm font-medium transition-colors ${
                    active
                      ? 'border-m-ink text-m-ink'
                      : 'border-transparent text-m-ink-muted hover:text-m-ink'
                  }`}
                >
                  <IconComponent className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="auth-field-label" htmlFor="contact-name">
                  Name <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Your full name"
                  required
                  className="auth-input"
                />
              </div>
              <div>
                <label className="auth-field-label" htmlFor="contact-email">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="you@example.com"
                  required
                  className="auth-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label className="auth-field-label" htmlFor="contact-phone">
                  Phone
                </label>
                <input
                  id="contact-phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="+1 (555) 000-0000"
                  className="auth-input"
                />
              </div>
              <div>
                <label className="auth-field-label" htmlFor="contact-subject">
                  Subject <span className="text-red-500">*</span>
                </label>
                <input
                  id="contact-subject"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Brief description"
                  required
                  className="auth-input"
                />
              </div>
            </div>

            <div>
              <label className="auth-field-label" htmlFor="contact-message">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                id="contact-message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Tell us more about your inquiry…"
                rows={5}
                required
                className="auth-textarea"
              />
            </div>

            <div className="flex flex-col gap-4 pt-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-start gap-2 text-xs" style={{ color: '#6b6b6b' }}>
                <Clock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>We typically reply within 24 hours on business days.</span>
              </div>
              <button
                type="submit"
                disabled={isSubmitting}
                className="marketing-btn marketing-btn-ink w-full gap-2 sm:w-auto disabled:opacity-60"
              >
                <Send className="h-4 w-4" />
                {isSubmitting ? 'Sending…' : 'Send message'}
              </button>
            </div>
          </form>
        </MarketingReveal>
      </MarketingSection>
    </MarketingPage>
  );
};

export default ContactUs;
